import { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import LogoutIcon from '@chronos/components/Icons/LogoutIcon';
import HamburgerIcon from '@chronos/components/Icons/HamburgerIcon';
import { DarkModeIcon } from '@chronos/components/Icons/DarkModeIcon';
import MenuLink from './MenuLink';
import BoardsSelect from '../BoardsSelect';
import * as S from './styled';
import Header from '../Header';
import Boards from './Boards';

const Sidebar = ({
  boards,
  boardId,
  liveWorklogs,
  onChangeBoard,
  hideSidebar,
  onLogout,
  collapsed,
  onChangeTheme,
  theme,
}) => {
  const headerIcons = [
    {
      name: 'hamburger',
      Icon: HamburgerIcon,
      size: ['24px', '24px'],
      callback: hideSidebar,
      placement: 'left',
    },
    {
      name: 'darkMode',
      Icon: DarkModeIcon,
      size: ['24px', '24px'],
      callback: onChangeTheme,
      placement: 'right',
      props: {
        color: theme === 'dark' ? 'white' : 'black',
      },
    },
    {
      name: 'logout',
      Icon: LogoutIcon,
      size: ['24px', '24px'],
      callback: onLogout,
      placement: 'right',
    },
  ];
  const currentBoard = useMemo(
    () => boards.find((board) => board.id === boardId),
    [boards, boardId]
  );

  const handleChangeBoard = useCallback(
    (id) => {
      hideSidebar();
      if (boardId !== id) {
        onChangeBoard(id);
      }
    },
    [boardId]
  );

  // TODO: rename variable
  const boardsInMenu = useMemo(() => {
    const items = [];
    boards.forEach((board) => {
      const boardHasLiveWorklogs = liveWorklogs.some(
        (worklog) => worklog.boardId === board.id
      );
      if ((board.starred || boardHasLiveWorklogs) && !items.includes(board)) {
        items.push({
          id: board.id,
          name: board.name,
          starred: board.starred,
          hasLiveWorklogs: boardHasLiveWorklogs,
        });
      }
    });
    return items;
  }, [boards, liveWorklogs]);

  return (
    <S.Sidebar>
      <Header
        title={currentBoard.name}
        toggleMenu={hideSidebar}
        icons={headerIcons}
        collapsed={collapsed}
      />
      <S.Menu>
        <S.MenuItem>
          <MenuLink
            to={`/${boardId}`}
            onClick={hideSidebar}
          >
            Work Log
          </MenuLink>
        </S.MenuItem>
        <S.MenuItem>
          <MenuLink
            to={`/${boardId}/cards`}
            onClick={hideSidebar}
          >
            Cards
          </MenuLink>
        </S.MenuItem>
        <S.MenuItem>
          <MenuLink
            to={`/${boardId}/mycards`}
            onClick={hideSidebar}
          >
            My Cards
          </MenuLink>
        </S.MenuItem>
        <Boards
          boards={boardsInMenu}
          handleClick={handleChangeBoard}
        />
      </S.Menu>

      <BoardsSelect
        boards={boards}
        value={boardId}
        onChange={handleChangeBoard}
      />
    </S.Sidebar>
  );
};

Sidebar.defaultProps = {
  boards: [],
  liveWorklogs: [],
  boardId: null,
  collapsed: false,
};

Sidebar.propTypes = {
  hideSidebar: PropTypes.func,
  onLogout: PropTypes.func,
  onChangeBoard: PropTypes.func.isRequired,
  onChangeTheme: PropTypes.func.isRequired,
  boards: PropTypes.array,
  boardId: PropTypes.string,
  collapsed: PropTypes.bool,
  liveWorklogs: PropTypes.array,
  theme: PropTypes.oneOf(['light', 'dark']),
};

export default Sidebar;
